"use client"

import { usePathname, useRouter } from "next/navigation"
import { createContext, useCallback, useContext, useEffect, useState } from "react"

interface PageTransitionContextValue {
  navigateWithFade: (href: string) => void
  isLeaving: boolean
  isEntering: boolean
}

const PageTransitionContext = createContext<PageTransitionContextValue>({
  navigateWithFade: () => {},
  isLeaving: false,
  isEntering: true,
})

export function usePageTransition() {
  return useContext(PageTransitionContext)
}

/** context提供のみ。フェードdivはPageTransitionWrapperで別途使う */
export function PageTransitionProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const router = useRouter()
  const pathname = usePathname()
  const [isLeaving, setIsLeaving] = useState(false)
  const [isEntering, setIsEntering] = useState(true)

  useEffect(() => {
    setIsEntering(true)
    setIsLeaving(false)
    const timer = setTimeout(() => setIsEntering(false), 600)
    return () => clearTimeout(timer)
  }, [pathname])

  const navigateWithFade = useCallback(
    (href: string) => {
      if (href === pathname) return
      setIsLeaving(true)
      setTimeout(() => router.push(href), 500)
    },
    [router, pathname]
  )

  return (
    <PageTransitionContext.Provider value={{ navigateWithFade, isLeaving, isEntering }}>
      {children}
    </PageTransitionContext.Provider>
  )
}

/** main+footerを包む。ヘッダーは包まない */
export function PageTransitionWrapper({
  children,
}: {
  children: React.ReactNode
}) {
  const { isLeaving, isEntering } = usePageTransition()

  return (
    <div
      className={
        isLeaving
          ? "transition-all duration-500 ease-in-out opacity-0 translate-y-2"
          : isEntering
            ? "animate-page-fade-in"
            : ""
      }
    >
      <style jsx>{`
        @keyframes pageFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-page-fade-in { animation: pageFadeIn 0.6s ease-out; }
      `}</style>
      {children}
    </div>
  )
}
